import { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  useToast,
  Text,
  FormLabel,
  FormControl,
  InputGroup,
  InputLeftAddon,
} from "@chakra-ui/react";
import { BrandButton } from "../BrandButton/BrandButton";
import { getAxiosClient } from "../../services/HttpsService";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/services/state/store";
import { useParams } from "react-router-dom";
import { fetchConnectedSystemsByApplication } from "../../services/state/connected-systems/connectedSystem-async-calls";

export const AddConnectedSystemModal = ({
  isOpen,
  onClose,
  edit = false,
  tenant,
}: {
  isOpen: boolean;
  onClose: () => void;
  edit?: boolean;
  tenant?: any;
}) => {
  const initialRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const { applicationId } = useParams();

  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const selectedApplication = useSelector(
    (state: RootState) => state.applications.selectedApplication
  );

  useEffect(() => {
    if (edit && tenant) {
      setName(tenant.name);
    }
  }, [edit, tenant]);

  const onCreate = async () => {
    if (name !== "") {
      const url = "/api/conn-systems";
      const data = {
        csNm: selectedApplication?.appPrefix
          ? selectedApplication.appPrefix + "_" + name
          : name,
        appUuid: applicationId,
      };

      setLoading(true); // Start loading
      try {
        const response = await getAxiosClient().post(url, data);

        if (response.status === 201) {
          toast({
            title: "Successfully created new connected system",
            description: "Created new connected system with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
          dispatch(fetchConnectedSystemsByApplication(applicationId as string));
          setName("");
        } else {
          toast({
            title: "Some error has occured",
            description: "Please try again",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error creating tenant:", error);
        toast({
          title: "Error",
          description: "An error occurred while creating new connected system.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false); // Stop loading
      }
    } else {
      toast({
        title: "Name is required",
        description: "Please enter a name and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onUpdate = async () => {
    if (name !== "") {
      const url = `/tenant/${tenant?.id}`;
      const data = {
        isActive: true,
      };

      setLoading(true); // Start loading
      try {
        const response = await getAxiosClient().put(url, data);
        if (response.status === 202) {
          toast({
            title: "Successfully updated connected system",
            description: "Updated connected system with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
          dispatch(fetchConnectedSystemsByApplication(applicationId as string));
          setName("");
        } else {
          toast({
            title: "Some error has occured",
            description: "Please try again",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error updating connected system:", error);
        toast({
          title: "Error",
          description: "An error occurred while updating new connected system.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false); // Stop loading
      }
    } else {
      toast({
        title: "Name is required",
        description: "Please enter a name and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={initialRef}
      size="xl"
      isCentered
    >
      <ModalOverlay
        zIndex={1}
        backdropFilter="blur(10px)" // ← This will blur the page behind the modal
        bg="rgba(0, 0, 0, 0.4)"
      />
      <ModalContent
        bg="rgba(0, 0, 0, 0.9)"
        backdropFilter="blur(20px)"
        border="1px solid rgba(255, 255, 255, 0.4)"
        borderRadius="20px"
        color="white"
      >
        <ModalCloseButton color="white" />
        <ModalBody>
          {edit ? (
            <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
              Edit Connected System
            </Text>
          ) : (
            <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
              Create New Connected System
            </Text>
          )}
          <FormControl isRequired>
            <FormLabel color="white">Name</FormLabel>
            <InputGroup>
              {selectedApplication?.appPrefix && (
                <InputLeftAddon
                  bg="rgba(255, 255, 255, 0.1)"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  color="white"
                >
                  {selectedApplication.appPrefix}_
                </InputLeftAddon>
              )}
              <Input
                placeholder="Enter name of the connected system"
                focusBorderColor="brand.500"
                ref={initialRef}
                fontSize="18px"
                value={name}
                onChange={(e) => setName(e.target.value)}
                overflowX="auto"
                isRequired
                disabled={edit}
                bg={
                  edit
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(255, 255, 255, 0.1)"
                }
                borderColor="rgba(255, 255, 255, 0.2)"
                color="white"
                _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                _focus={{
                  borderColor: "brand.500",
                  boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                }}
                _disabled={{
                  bg: "rgba(255, 255, 255, 0.05)",
                  color: "rgba(255, 255, 255, 0.5)",
                }}
              />
            </InputGroup>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          {edit ? (
            <BrandButton
              onClick={onUpdate}
              isLoading={loading}
              isDisabled={loading}
            >
              Save Changes
            </BrandButton>
          ) : (
            <BrandButton
              onClick={onCreate}
              isLoading={loading}
              isDisabled={loading}
            >
              Create
            </BrandButton>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
